import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { useTriggerContentScoring } from '~/features/content-enrichment/hooks/use-content-scoring';
import { useExperimentPostStepFlagsStore } from '~/features/experiments/store/experiment-post-step-flags.store';
import { compactEnrichmentFlags } from '~/types/enrichment-flags';
import { extractApiError } from '~/utils/extract-api-error';

import { createExperimentDraft, updateExperimentDraft } from '../services/experiment-draft.service';
import type { DraftExperiment } from '../types/experiment-draft.types';
import { EXPERIMENT_POST_STEP_DEFAULTS } from '../utils/content-attribute-steps';
import { mapSourceToRestartUpdateBody } from '../utils/restart-payload';

interface RestartArgs {
  /** Full source draft DTO — supplies every config field the clone copies. */
  source: DraftExperiment;
  /** Ad-platform token for the create call (`GOOGLE_ADS` / `META_ADS`), resolved by the caller. */
  adPlatform: string;
  /** Operator-edited name for the clone; overrides the mapper's default when set. */
  name?: string;
  /** Optional arm-assignment CSV; when present the create is multipart. */
  armAssignmentFile?: File;
}

/**
 * Restart an experiment: clone a source experiment's config into a brand-new
 * run, optionally attaching an arm-assignment CSV, and launch it. The original
 * is left untouched. Mirrors the create flow's Review-&-Launch sequence so the
 * clone lands on the same content-enrichment funnel a fresh create would.
 *
 * Sequence (all awaited so a mid-flight failure surfaces one error and stops):
 *   1. Create the new draft — multipart when a file is attached (file rides the
 *      create POST, per contract; it is immutable afterwards).
 *   2. PATCH the copied config onto the new draft.
 *   3. PATCH `status: CREATED` to transition draft → real experiment.
 *   4. Record post-step flags + trigger content scoring, exactly as launch does.
 *   5. Navigate to the new experiment's content-enrichment funnel.
 */
export function useRestartExperiment() {
  const { t } = useTranslation('experiments');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recordPostStepFlags = useExperimentPostStepFlagsStore((s) => s.setFlags);
  const triggerScoring = useTriggerContentScoring();

  return useMutation({
    mutationFn: async ({
      source,
      adPlatform,
      name,
      armAssignmentFile,
    }: RestartArgs): Promise<string> => {
      const created = await createExperimentDraft({
        parentIdField: source.parentIdField,
        adPlatform,
        ...(armAssignmentFile ? { armAssignmentFile } : {}),
      });
      // Clone the config; a supplied name overrides the mapper's default suffix.
      await updateExperimentDraft(created.id, {
        ...mapSourceToRestartUpdateBody(source),
        ...(name ? { name } : {}),
      });
      await updateExperimentDraft(created.id, { status: 'CREATED', stage: 'REVIEW_AND_LAUNCH' });
      // Post-step flags default to the same "scoring pinned off" baseline a fresh
      // launch uses; recorded before the trigger so the funnel can read them.
      const flags = compactEnrichmentFlags(EXPERIMENT_POST_STEP_DEFAULTS);
      recordPostStepFlags(created.id, flags);
      await triggerScoring.mutateAsync({ expId: created.id, payload: flags });
      return created.id;
    },
    onSuccess: (newExperimentId) => {
      // Refresh the listing so the new run appears, then land on its funnel.
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void navigate({
        to: '/experiments/$expId/content-enrichment',
        params: { expId: newExperimentId },
      });
    },
    onError: (err) => {
      // Backend errors are 400 `{ message, errors[] }` — `extractApiError`
      // surfaces the actionable message; falls back to a generic restart error.
      toast.error(extractApiError(err, t('detail.restart.error')));
    },
  });
}
